/**
 * 맥스매치 Full React Query 훅.
 */
import {
  maxMatchFullService,
  type MaxMatchFullSearchParams,
  type MaxMatchFullSourceReplaceRequest,
} from '@/entities/max-match/api/max-match-full.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const maxMatchFullKeys = {
  all: ['max-match-full'] as const,
  lists: () => [...maxMatchFullKeys.all, 'list'] as const,
  detail: (id: number) => [...maxMatchFullKeys.all, 'detail', id] as const,
  run: (runId: number) => [...maxMatchFullKeys.all, 'run', runId] as const,
};

export const useMaxMatchFullList = (
  search: MaxMatchFullSearchParams,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: maxMatchFullKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      maxMatchFullService.search(s as MaxMatchFullSearchParams, p),
    enabled: Boolean(search.yearMonth),
  });

export const useMaxMatchFullDetail = (maxMatchFullPrescriptionId: number) =>
  useQuery({
    queryKey: maxMatchFullKeys.detail(maxMatchFullPrescriptionId),
    queryFn: () => maxMatchFullService.getDetail(maxMatchFullPrescriptionId),
    enabled: maxMatchFullPrescriptionId > 0,
    staleTime: 0,
  });

export const useReplaceMaxMatchFullSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      maxMatchFullPrescriptionId,
      payload,
    }: {
      maxMatchFullPrescriptionId: number;
      payload: MaxMatchFullSourceReplaceRequest;
    }) =>
      maxMatchFullService.replaceSource(maxMatchFullPrescriptionId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: maxMatchFullKeys.detail(variables.maxMatchFullPrescriptionId),
      });
      queryClient.invalidateQueries({ queryKey: maxMatchFullKeys.lists() });
    },
  });
};

export const useConfirmMaxMatchFullImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      maxMatchFullPrescriptionId,
      imageId,
    }: {
      maxMatchFullPrescriptionId: number;
      imageId: number;
    }) => maxMatchFullService.confirmImage(maxMatchFullPrescriptionId, imageId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: maxMatchFullKeys.detail(variables.maxMatchFullPrescriptionId),
      });
      queryClient.invalidateQueries({ queryKey: maxMatchFullKeys.lists() });
    },
  });
};

export const useMarkMaxMatchFullImageUnverifiable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      maxMatchFullPrescriptionId,
      imageId,
    }: {
      maxMatchFullPrescriptionId: number;
      imageId: number;
    }) =>
      maxMatchFullService.markImageUnverifiable(
        maxMatchFullPrescriptionId,
        imageId,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: maxMatchFullKeys.detail(variables.maxMatchFullPrescriptionId),
      });
      queryClient.invalidateQueries({ queryKey: maxMatchFullKeys.lists() });
    },
  });
};

export const useConfirmMaxMatchFullBatch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      maxMatchFullPrescriptionId,
      imageIds,
    }: {
      maxMatchFullPrescriptionId: number;
      imageIds: number[];
    }) =>
      maxMatchFullService.confirmBatch(maxMatchFullPrescriptionId, imageIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: maxMatchFullKeys.detail(variables.maxMatchFullPrescriptionId),
      });
      queryClient.invalidateQueries({ queryKey: maxMatchFullKeys.lists() });
    },
  });
};

export const useRunMaxMatchFull = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (maxMatchFullPrescriptionId: number) =>
      maxMatchFullService.run(maxMatchFullPrescriptionId),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: maxMatchFullKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: maxMatchFullKeys.lists() });
    },
  });
};

export const useDownloadMaxMatchFullExcel = () =>
  useMutation({
    mutationFn: (runId: number) => maxMatchFullService.getExcelDetails(runId),
  });
